import { useEffect, useRef, useState } from 'react';
import { useAppContext } from 'scbk-services/shared-context';

/**
 * callback 함수 타입 정의
 */
type PageReadyCallback = () => void | Promise<void>;

/**
 * usePageReady Options 타입 정의
 */
type UsePageReadyOptions = {
    enabled?: boolean;
    deps?: React.DependencyList;
};

/**
 * usePageReady result 타입 정의
 */
type UsePageReadyResult = {
    isReady: boolean;
    isLoading: boolean;
    error: unknown | null;
};

/**
 * 페이지 초기화 처리 hook
 * @param callback
 * @param options
 * @returns
 */
const usePageReady = (
    callback: PageReadyCallback,
    options?: UsePageReadyOptions,
): UsePageReadyResult => {
    const { enabled = true, deps = [] } = options ?? {};
    const { initialized } = useAppContext();

    const [isReady, setIsReady] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<unknown | null>(null);
    const callbackRef = useRef(callback);

    useEffect(() => {
        console.log('#### usePageReady MOUNT');

        return () => {
            console.log('#### usePageReady UNMOUNT');
        };
    }, []);

    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    /**
     * 초기화 수행
     */
    useEffect(() => {
        if (!enabled) {
            return;
        }

        if (!initialized) {
            console.log('### usePageReady app not initialized!');
            return;
        }

        let cancelled = false;

        const run = async () => {
            setIsReady(false);
            setIsLoading(true);
            setError(null);

            try {
                //초기화 콜백 함수 실행
                const result = callbackRef.current();
                if (result instanceof Promise) {
                    await result;
                }

                if (!cancelled) {
                    setIsReady(true);
                }
            } catch (e) {
                if (!cancelled) {
                    setError(e);
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        run();

        return () => {
            //언마운트 시 처리
            cancelled = true;
        };
    }, [enabled, initialized, ...deps]);

    return {
        isReady,
        isLoading,
        error,
    };
};

export { usePageReady };

export type { UsePageReadyOptions, UsePageReadyResult };
